"use client";

import { Suspense } from "react";
import { useTranslations } from "next-intl";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { DesktopSearchBar } from "./DesktopSearchBar";
import { MobileSearch, type MobileTrigger } from "./MobileSearch";

/*
 * 검색바는 useSearchParams 를 쓰므로, 정적 렌더(ISR) 페이지에서는 가장 가까운 Suspense
 * 경계까지 클라이언트 렌더로 넘어간다. 경계를 여기 두어 그 범위를 검색바로 한정하고,
 * 서버 HTML 에는 같은 크기의 정적 외형을 넣는다(하이드레이션 때 밀림 없음).
 */

function DesktopFallback() {
  const t = useTranslations("searchComposer");
  const seg = (label: string, value: string, grow = "flex-1") => (
    <div className={`flex h-full min-w-0 ${grow} flex-col justify-center pl-6 pr-2 lg:pl-7`}>
      <span className="text-xs font-semibold text-gray-900">{label}</span>
      <span className="truncate text-sm text-gray-500">{value}</span>
    </div>
  );
  return (
    <div className="flex h-16 w-full items-center rounded-full border border-gray-200 bg-white shadow-[0_3px_12px_rgba(0,0,0,0.08)]">
      {seg(t("location"), t("locationEmpty"))}
      {seg(t("date"), t("dateEmpty"))}
      {seg(t("what"), t("whatEmpty"), "flex-[1.3]")}
      <span className="mr-2 flex size-12 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white">
        <MagnifyingGlassIcon className="size-4.5" weight="bold" />
      </span>
    </div>
  );
}

function MobileFallback({ trigger }: { trigger: MobileTrigger }) {
  const t = useTranslations("searchComposer");
  if (trigger === "icon") {
    return (
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full">
        <MagnifyingGlassIcon className="size-5" />
      </span>
    );
  }
  if (trigger === "summary") {
    // 검색 결과 상단 두 줄 알약과 같은 높이(h-14) — 로드 순간 헤더가 커지며 튀지 않게
    return (
      <div className="flex h-14 w-full flex-col items-center justify-center rounded-full border border-gray-200 bg-white px-5 shadow-[0_2px_10px_rgba(0,0,0,0.08)]">
        <span className="h-3.5 w-20 animate-pulse rounded bg-gray-100" />
        <span className="mt-1.5 h-3 w-32 animate-pulse rounded bg-gray-100" />
      </div>
    );
  }
  return (
    <div
      className={
        trigger === "pill"
          ? "flex h-13 w-full items-center justify-center gap-2 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-900 shadow-[0_4px_14px_rgba(0,0,0,0.1)]"
          : "flex h-11 w-full items-center gap-2.5 rounded-full border border-gray-200 bg-white px-4 text-sm text-gray-500 shadow-[0_2px_10px_rgba(0,0,0,0.08)]"
      }
    >
      <MagnifyingGlassIcon className="size-4.5 shrink-0" weight="bold" />
      <span className="truncate">{t("mobileEntry")}</span>
    </div>
  );
}

export function SearchComposerDesktop({ compact = false }: { compact?: boolean }) {
  return (
    <Suspense fallback={compact ? <CompactFallback /> : <DesktopFallback />}>
      <DesktopSearchBar compact={compact} />
    </Suspense>
  );
}

function CompactFallback() {
  const t = useTranslations("searchComposer");
  return (
    <div className="flex h-16 w-full items-center justify-center">
      <div className="flex h-12 items-center rounded-full border border-gray-200 bg-white pl-2 text-sm text-gray-500 shadow-[0_1px_2px_rgba(0,0,0,0.08),0_4px_12px_rgba(0,0,0,0.05)]">
        <span className="px-4">{t("compactWhere")}</span>
        <span className="border-l border-gray-300 px-4">{t("compactWhen")}</span>
        <span className="border-l border-gray-300 px-4">{t("compactWhat")}</span>
        <span className="ml-1 mr-1.5 flex size-8.5 items-center justify-center rounded-full bg-orange-500 text-white">
          <MagnifyingGlassIcon className="size-4" weight="bold" />
        </span>
      </div>
    </div>
  );
}

export function SearchComposerMobile(props: {
  trigger: MobileTrigger;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <Suspense fallback={<MobileFallback trigger={props.trigger} />}>
      <MobileSearch {...props} />
    </Suspense>
  );
}
export { FilterNavButton } from "./FilterNavButton";
